/**
 * ContactInfoBlock
 * Props-injected: requires `siteData` prop instead of useSite() hook.
 * siteData: { contactInfo: { phone, email, address }, businessHours: [] }
 * This allows the block to work in both the ecommerce site (via SiteContext)
 * and the back-office WYSIWYG editor (via direct props).
 */
import React from 'react';
import styled from 'styled-components';
import { Section, Container, BlockTitle, t } from '../styles/shared';
import { mapEmbedSrc } from './mapEmbedSrc';

const Grid = styled.div`
  display: flex; flex-direction: column; gap: 24px;
  ${({ $layout }) => ($layout === 'horizontal' || $layout === 'cards') && 'flex-direction: row; flex-wrap: wrap;'}
`;

const InfoItem = styled.div`
  display: flex; gap: 16px;
  ${({ $layout }) => $layout === 'cards' && `
    flex: 1; min-width: 200px;
    background: ${t.bgAlt}; padding: 24px; border-radius: 8px;
  `}
`;

const IconCircle = styled.div`
  flex-shrink: 0; width: 48px; height: 48px;
  display: flex; align-items: center; justify-content: center;
  background: ${t.primary}; border-radius: 50%; color: #fff;
  svg { width: 24px; height: 24px; }
`;

const TextWrap = styled.div`
  h4 { margin: 0 0 4px 0; font-size: 0.875rem; font-weight: 600;
       text-transform: uppercase; letter-spacing: 0.5px; color: ${t.textLight}; }
  p  { margin: 0; font-size: 1rem; color: ${t.text}; white-space: pre-line; }
  a  { color: ${t.primary}; text-decoration: none; }
  a:hover { text-decoration: underline; }
`;

const HoursList = styled.ul`
  list-style: none; padding: 0; margin: 0;
  li { display: flex; justify-content: space-between; gap: 16px; padding: 4px 0; }
  .day  { font-weight: 500; }
  .time { color: ${t.textLight}; }
`;

const MapFrame = styled.div`
  margin-top: 24px;
  border-radius: 8px;
  overflow: hidden;
  line-height: 0;
  iframe { width: 100%; height: 300px; border: 0; }
`;

const PhoneIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>;
const EmailIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const MapIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const ClockIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;

const ContactInfoBlock = ({ content = {}, settings = {}, siteData = {} }) => {
  const {
    title = 'Contact Information',
    showPhone = true, showEmail = true, showAddress = true, showHours = true,
    showMap = false,
    layout = 'vertical',
    customPhone = '', customEmail = '', customAddress = ''
  } = content;
  const { backgroundColor = '', padding = 'medium' } = settings;

  const phone   = customPhone   || siteData.contactInfo?.phone   || '';
  const email   = customEmail   || siteData.contactInfo?.email   || '';
  const address = customAddress || siteData.contactInfo?.address || '';
  const hours   = siteData.businessHours || [];
  const mapSrc  = showMap ? mapEmbedSrc(address) : '';

  return (
    <Section $padding={padding} $bgColor={backgroundColor}>
      <Container>
        {title && <BlockTitle>{title}</BlockTitle>}
        <Grid $layout={layout}>
          {showPhone && phone && (
            <InfoItem $layout={layout}><IconCircle><PhoneIcon /></IconCircle>
              <TextWrap><h4>Phone</h4><p><a href={`tel:${phone.replace(/[^0-9+]/g,'')}`}>{phone}</a></p></TextWrap>
            </InfoItem>
          )}
          {showEmail && email && (
            <InfoItem $layout={layout}><IconCircle><EmailIcon /></IconCircle>
              <TextWrap><h4>Email</h4><p><a href={`mailto:${email}`}>{email}</a></p></TextWrap>
            </InfoItem>
          )}
          {showAddress && address && (
            <InfoItem $layout={layout}><IconCircle><MapIcon /></IconCircle>
              <TextWrap><h4>Address</h4><p>{address}</p></TextWrap>
            </InfoItem>
          )}
          {showHours && hours.length > 0 && (
            <InfoItem $layout={layout}><IconCircle><ClockIcon /></IconCircle>
              <TextWrap><h4>Hours</h4>
                <HoursList>
                  {hours.map((h, i) => <li key={i}><span className="day">{h.day}</span><span className="time">{h.hours}</span></li>)}
                </HoursList>
              </TextWrap>
            </InfoItem>
          )}
        </Grid>
        {mapSrc && (
          <MapFrame>
            <iframe
              src={mapSrc}
              title={`Map of ${address.replace(/\s+/g, ' ').trim()}`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </MapFrame>
        )}
      </Container>
    </Section>
  );
};

export default ContactInfoBlock;
